import { useState, useEffect } from 'react';
import { X, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AIMusicSuggestionModal } from './AIMusicSuggestionModal';

type CreatePinModalProps = {
  latitude: number;
  longitude: number;
  onClose: () => void;
  onSuccess: () => void;
};

export function CreatePinModal({ latitude, longitude, onClose, onSuccess }: CreatePinModalProps) {
  const { user, profile } = useAuth();
  const { t, language } = useLanguage();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [locationName, setLocationName] = useState('');
  const [tags, setTags] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [radius, setRadius] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAISuggestion, setShowAISuggestion] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showAISuggestion) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, showAISuggestion]);

  const extractVideoId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!user) {
        throw new Error('You must be logged in to create a pin');
      }

      const videoId = extractVideoId(youtubeUrl);
      if (!videoId) {
        throw new Error('Invalid YouTube URL');
      }

      const { error: insertError } = await supabase
        .from('audio_pins')
        .insert({
          user_id: user.id,
          title: title.trim(),
          description: description.trim() || null,
          youtube_url: youtubeUrl.trim(),
          youtube_video_id: videoId,
          latitude,
          longitude,
          location: `POINT(${longitude} ${latitude})`,
          location_name: locationName.trim() || null,
          tags: tags.split(',').map(tag => tag.trim()).filter(Boolean),
          is_public: isPublic,
          radius: radius,
        });

      if (insertError) throw insertError;

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create audio pin');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">{t('createPin.title')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-300"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.pinTitle')} *
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                required
                maxLength={200}
              />
            </div>

            <div>
              <label htmlFor="youtubeUrl" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.youtubeUrl')} *
              </label>
              <input
                id="youtubeUrl"
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder={t('createPin.youtubeUrlPlaceholder')}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.description')}
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('createPin.descriptionPlaceholder')}
                rows={3}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
                maxLength={500}
              />
            </div>

            <div>
              <label htmlFor="locationName" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.location')}
              </label>
              <input
                id="locationName"
                type="text"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                placeholder="e.g., Central Park, NYC"
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-300 mb-1">
                {t('createPin.tags')}
              </label>
              <input
                id="tags"
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder={t('createPin.tagsPlaceholder')}
                className="w-full px-4 py-2 border bg-gray-700 border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-[#00c7b7] focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="radius" className="block text-sm font-medium text-gray-300 mb-1">
                Listening Radius <span className="text-gray-400 text-xs">(5-50 meters)</span>
              </label>
              <input
                id="radius"
                type="range"
                min="5"
                max="50"
                value={radius}
                onChange={(e) => setRadius(parseInt(e.target.value))}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>5m</span>
                <span className="font-medium text-[#00c7b7]">{radius}m</span>
                <span>50m</span>
              </div>
            </div>

            <div className="flex items-center">
              <input
                id="isPublic"
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="w-4 h-4 text-[#00c7b7] border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="isPublic" className="ml-2 text-sm text-gray-300">
                Make this pin public
              </label>
            </div>

            <div className="text-sm text-gray-300 bg-gray-900 p-3 rounded-lg">
              <p>Location: {latitude.toFixed(6)}, {longitude.toFixed(6)}</p>
            </div>

            {profile?.role === 'admin' && (
              <button
                type="button"
                onClick={() => setShowAISuggestion(true)}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" />
                {language === 'ko' ? 'AI 음악 추천 받기' : 'Get AI Music Suggestions'}
              </button>
            )}

            {error && (
              <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 border border-gray-600 text-gray-300 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
              >
                {t('createPin.cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-[#00c7b7] hover:bg-[#14f0d5] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('createPin.creating') : t('createPin.create')}
              </button>
            </div>
          </form>
        </div>
      </div>

      <AIMusicSuggestionModal
        isOpen={showAISuggestion}
        onClose={() => setShowAISuggestion(false)}
        onSelect={async (suggestions) => {
          try {
            setLoading(true);
            setError('');
            setShowAISuggestion(false); // Close AI modal immediately

            for (const suggestion of suggestions) {
              if (!suggestion.videoId) {
                console.warn('Skipping suggestion without videoId:', suggestion.title);
                continue;
              }

              const { error: insertError } = await supabase
                .from('audio_pins')
                .insert({
                  user_id: user!.id,
                  title: suggestion.title,
                  description: suggestion.aiReason || suggestion.description.substring(0, 500),
                  youtube_url: suggestion.mediaUrl,
                  youtube_video_id: suggestion.videoId,
                  latitude,
                  longitude,
                  location: `POINT(${longitude} ${latitude})`,
                  location_name: locationName.trim() || null,
                  tags: [],
                  is_public: true,
                  source: 'ai',
                  ai_reason: suggestion.aiReason,
                  color_override: '#9333ea',
                });

              if (insertError) {
                console.error('Error inserting pin:', insertError);
                setError(`Failed to create pin: ${suggestion.title}`);
              }
            }

            // Wait a bit for state updates to complete
            await new Promise(resolve => setTimeout(resolve, 100));

            onSuccess();
            onClose();
          } catch (err: any) {
            console.error('Error creating pins:', err);
            setError(err.message || 'Failed to create audio pins');
            setLoading(false);
          }
        }}
        locationName={locationName}
        latitude={latitude}
        longitude={longitude}
      />
    </div>
  );
}
